import { useId, useRef, type FocusEvent, type KeyboardEvent } from 'react';
import { TICKET_TYPE_GLYPHS, ticketTypeGlyphLabel } from '@vd/annotation-core';
import { cn } from '@vd/ui';
import { TypeMarker } from './TypeMarker';
import styles from './MarkerPicker.module.css';

/** The keys that move a radio group's selection, each with a click of the browser's own. */
const ARROWS: ReadonlySet<string> = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/**
 * A ticket type's marker, picked from a grid of the markers themselves
 * (TOADO-526, Toado ticket t-eWMzyv) rather than from a list of their names.
 *
 * Radio buttons underneath, so the arrow keys move through the grid and a
 * screen reader hears each marker's name. A marker this build does not list
 * (one chosen on a newer build) stays as an extra choice, so opening the page
 * cannot silently change it.
 *
 * `onChange` follows the selection, which the arrow keys move one marker at a
 * time. `onCommit` is a decision: a click on a marker (by pointer, or by a
 * screen reader, which clicks without a key), or Enter or Space, as the key
 * comes up. A picker that
 * saves as soon as it is used (in a popover) listens to `onCommit`, or every
 * arrow press would save, and close it, on the way past.
 */
export function MarkerPicker({ value, onChange, onCommit, label = 'Marker', disabled = false, autoFocus = false }: {
  value: string;
  onChange: (glyph: string) => void;
  onCommit?: (glyph: string) => void;
  label?: string;
  disabled?: boolean;
  /** Focus the chosen marker on mount, for a picker that opens in a popover. */
  autoFocus?: boolean;
}) {
  const name = useId();
  /**
   * Set while a key that makes a browser click on its own is being handled: an
   * arrow, which moves the selection with a click in the same task, and Space as
   * it comes up, in some browsers. Those clicks are not decisions. No other key
   * holds a click back, and the hold ends in the next task, so a screen reader's
   * click, which comes with no key, still commits.
   */
  const keyed = useRef(false);
  /**
   * The Enter or Space that went down, and the marker it went down on. It
   * commits as it comes up, not as it goes down: a commit closes a popover and
   * gives its button the focus, and a key still held would repeat into that
   * button and open it again (review round 3). Only a key that went down on a
   * marker here can commit, so the Enter that opened the popover (it went down
   * on the button) does not as it comes up on the marker the popover focused;
   * and it commits only on the marker it went down on, being dropped once focus
   * leaves that marker (an arrow, Escape or Tab while the key is held), as a
   * button's press is (review round 4).
   */
  const pressed = useRef<{ key: string; target: HTMLInputElement } | null>(null);
  const options = TICKET_TYPE_GLYPHS.some((option) => option.value === value)
    ? TICKET_TYPE_GLYPHS
    : [...TICKET_TYPE_GLYPHS, { value, label: value }];

  const radio = (target: EventTarget): target is HTMLInputElement =>
    target instanceof HTMLInputElement && target.type === 'radio';
  const swallowKeyClick = () => {
    keyed.current = true;
    window.setTimeout(() => { keyed.current = false; }, 0);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLFieldSetElement>) => {
    if (!onCommit || !radio(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // A held key repeats: one decision, made as it comes up.
      if (!event.repeat) pressed.current = { key: event.key, target: event.target };
      return;
    }
    if (ARROWS.has(event.key)) swallowKeyClick();
  };
  const onKeyUp = (event: KeyboardEvent<HTMLFieldSetElement>) => {
    if (!onCommit || !radio(event.target)) return;
    if (event.key === ' ') swallowKeyClick();
    const press = pressed.current;
    if ((event.key === 'Enter' || event.key === ' ') && press?.key === event.key) {
      pressed.current = null;
      if (press.target === event.target) onCommit(event.target.value);
    }
  };
  const onBlur = (event: FocusEvent<HTMLFieldSetElement>) => {
    // A radio's blur, bubbled to the fieldset: its target is the radio.
    const left: EventTarget = event.target;
    if (pressed.current?.target === left) pressed.current = null;
  };

  return (
    <fieldset className={styles.picker} disabled={disabled} onKeyDown={onKeyDown} onKeyUp={onKeyUp} onBlur={onBlur}>
      <legend className={styles.legend}>
        {label}: <span className={styles.current}>{ticketTypeGlyphLabel(value)}</span>
      </legend>
      <div className={styles.grid}>
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(styles.option, option.value === value && styles.selected)}
            title={option.label}
          >
            <input
              type="radio"
              className={styles.input}
              name={name}
              value={option.value}
              checked={option.value === value}
              autoFocus={autoFocus && option.value === value}
              onChange={() => onChange(option.value)}
              onClick={() => {
                if (keyed.current) {
                  keyed.current = false;
                  return;
                }
                onCommit?.(option.value);
              }}
              aria-label={option.label}
            />
            <TypeMarker glyph={option.value} height={30} />
          </label>
        ))}
      </div>
    </fieldset>
  );
}
